/**
 * KAN-754: чистые функции форм ручного товара и категории каталога:
 * значения формы, клиентская валидация, тело запроса, разбор ошибок
 * сервера по полям, плоский список категорий для выбора.
 */
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { safeHttpUrl } from '@/lib/safeUrl';
import type { CategoryNode } from '@/lib/catalogTree';
import type { CatalogProductBody, CatalogProductDetail } from '@/types/catalog';

export const PRODUCT_NAME_MAX = 1000;
export const PRODUCT_SKU_MAX = 255;
export const PRODUCT_URL_MAX = 1000;
export const CATEGORY_NAME_MAX = 500;

export interface ProductFormValues {
  name: string;
  price: string;
  sku: string;
  /** '' : без категории. */
  categoryId: string;
  available: boolean;
  stockQuantity: string;
  description: string;
  url: string;
  pictureUrl: string;
}

export type ProductFormField = keyof ProductFormValues;
export type ProductFormErrors = Partial<Record<ProductFormField, string>>;

export interface ProductFormResult {
  body: CatalogProductBody | null;
  errors: ProductFormErrors;
}

export function emptyProductForm(categoryId: string | null = null): ProductFormValues {
  return {
    name: '',
    price: '',
    sku: '',
    categoryId: categoryId ?? '',
    available: true,
    stockQuantity: '',
    description: '',
    url: '',
    pictureUrl: '',
  };
}

const text = (value: string | null | undefined): string => value ?? '';
const num = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : String(value);

export function productToForm(product: CatalogProductDetail): ProductFormValues {
  return {
    name: product.name,
    price: num(product.price),
    sku: text(product.sku),
    categoryId: product.category_id === null || product.category_id === undefined ? '' : String(product.category_id),
    available: product.available,
    stockQuantity: num(product.stock_quantity),
    description: text(product.description),
    url: text(product.url),
    pictureUrl: text(product.picture_url),
  };
}

const PRICE = /^\d+(\.\d{1,2})?$/;
/** Предел DecimalField(max_digits=12, decimal_places=2) на backend. */
export const PRODUCT_PRICE_MAX = 9999999999.99;
const INTEGER = /^\d+$/;

/** "1 500,5" -> 1500.5; пусто -> null; неверно -> undefined. */
function parsePrice(raw: string): number | null | undefined {
  const value = raw.replace(/\s/g, '').replace(',', '.');
  if (value === '') return null;
  if (!PRICE.test(value)) return undefined;
  const price = Number(value);
  return price <= PRODUCT_PRICE_MAX ? price : undefined;
}

function parseStock(raw: string): number | null | undefined {
  const value = raw.trim();
  if (value === '') return null;
  return INTEGER.test(value) ? Number(value) : undefined;
}

/** Ссылка необязательна, но если задана, то только http(s). */
function urlError(raw: string): string | undefined {
  const value = raw.trim();
  if (value === '' || safeHttpUrl(value)) return undefined;
  return 'Ссылка должна начинаться с http:// или https://';
}

export function validateProductForm(values: ProductFormValues): ProductFormResult {
  const errors: ProductFormErrors = {};
  const name = values.name.trim();
  if (!name) errors.name = 'Укажите название';

  const price = parsePrice(values.price);
  if (price === undefined) errors.price = 'Цена: число не меньше 0, до двух знаков после запятой';

  const stock = parseStock(values.stockQuantity);
  if (stock === undefined) errors.stockQuantity = 'Количество: целое число не меньше 0';

  const url = urlError(values.url);
  if (url) errors.url = url;
  const picture = urlError(values.pictureUrl);
  if (picture) errors.pictureUrl = picture;

  if (Object.keys(errors).length > 0) return { body: null, errors };
  return {
    errors,
    body: {
      name,
      price: price ?? null,
      sku: values.sku.trim(),
      category_id: values.categoryId || null,
      available: values.available,
      stock_quantity: stock ?? null,
      description: values.description.trim(),
      url: values.url.trim(),
      picture_url: values.pictureUrl.trim(),
    },
  };
}

export function validateCategoryName(name: string): string | null {
  return name.trim() ? null : 'Укажите название категории';
}

/** Поле ответа API -> поле формы. */
const SERVER_FIELDS: Record<string, string> = {
  name: 'name',
  price: 'price',
  sku: 'sku',
  category_id: 'categoryId',
  category: 'categoryId',
  parent_id: 'parentId',
  parent: 'parentId',
  available: 'available',
  stock_quantity: 'stockQuantity',
  description: 'description',
  url: 'url',
  picture_url: 'pictureUrl',
};

export interface ServerErrors {
  /** Ошибки по полям формы (ключ поля формы). */
  fields: Record<string, string>;
  /** Общая ошибка формы (detail, non_field_errors, сеть). */
  form: string | null;
}

const firstMessage = (value: unknown): string | null => {
  const list = Array.isArray(value) ? value : [value];
  const found = list.find((item): item is string => typeof item === 'string' && item !== '');
  return found ?? null;
};

/** Ответ 400 {field: [msg]} раскладывается по полям, остальное в общую ошибку. */
export function serverFieldErrors(err: unknown, fallback: string): ServerErrors {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return { fields: {}, form: extractErrorMessage(err, fallback) };
  }
  const fields: Record<string, string> = {};
  const rest: string[] = [];
  Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
    // code (например feed_readonly) машинный, пользователю только detail.
    if (key === 'code') return;
    const message = firstMessage(value);
    if (!message) return;
    const field = SERVER_FIELDS[key];
    if (field) fields[field] = message;
    else rest.push(message);
  });
  const form = rest.length > 0 ? rest.join(' ') : null;
  if (Object.keys(fields).length === 0 && !form) return { fields, form: extractErrorMessage(err, fallback) };
  return { fields, form };
}

export interface CategoryOption {
  value: string;
  label: string;
}

const INDENT = '   ';

/** Категории в порядке дерева, вложенность отступом. */
export function categoryOptions(tree: readonly CategoryNode[]): CategoryOption[] {
  const out: CategoryOption[] = [];
  const walk = (nodes: readonly CategoryNode[]): void =>
    nodes.forEach((node) => {
      out.push({ value: node.id, label: `${INDENT.repeat(node.depth)}${node.name}` });
      walk(node.children);
    });
  walk(tree);
  return out;
}
